/**
 * ImageField — Phase 19-B
 *
 * Replaces raw `<input type="text" value=image_url>` for product / variant
 * images. Lets users:
 *   - upload a new file from disk directly (one click, no library detour)
 *   - pick an existing image from the Media Library (modal)
 *   - remove the linked image
 *   - paste an external URL (collapsed "Advanced" toggle for power users)
 *
 * Storage stays a single `image_url` string field. New uploads land in the
 * Media Library and we store `/api/media/{id}`. External URLs continue to
 * work unchanged. No schema changes.
 *
 * Props:
 *   value             string  current image_url (may be absolute http(s) or /api/media/...)
 *   onChange          (url, item?) => void   item is present for fresh uploads/picks
 *   label             string                  default "Afbeelding"
 *   compact           bool                    smaller preview (variant rows)
 *   testIdPrefix      string                  for `data-testid`
 *   helperText        string                  optional under-field helper
 *
 * R4 compliance: this is itself a top-level functional component. Internal
 * sub-elements are simple JSX nodes, NOT inner functions, to avoid focus
 * loss when re-rendered by parent state.
 */
import React, { useRef, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Upload, ImageIcon, FolderOpen, X, Link as LinkIcon, Loader2,
} from "lucide-react";
import MediaPickerDialog from "./MediaPickerDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT_MIME = "image/png,image/jpeg,image/jpg,image/webp,image/gif";
const ALLOWED_MIME_SET = new Set([
  "image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif",
]);

function previewSrc(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  // Relative `/api/media/...` works both in admin (same origin) and public
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

export default function ImageField({
  value,
  onChange,
  label = "Afbeelding",
  compact = false,
  testIdPrefix = "image-field",
  helperText,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const hasImage = !!(value && String(value).trim().length > 0);

  const triggerFile = () => {
    if (!uploading && fileRef.current) fileRef.current.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Bestand is te groot. Maximaal 5 MB.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (!ALLOWED_MIME_SET.has(file.type)) {
      toast.error("Alleen JPG, PNG, WEBP of GIF zijn toegestaan.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt_text", "");
    fd.append("title", "");
    setUploading(true);
    try {
      const r = await adminApi.post("/admin/media/upload", fd, {
        headers: { "Content-Type": undefined },
      });
      const item = r.data;
      const url = item?.url || `/api/media/${item?.id}`;
      onChange?.(url, item);
      toast.success("Afbeelding gekoppeld");
    } catch (err) {
      toast.error(formatApiError(err, "Afbeelding kon niet worden geüpload."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onLibrarySelect = (item) => {
    const url = item?.url || `/api/media/${item?.id}`;
    onChange?.(url, item);
    toast.success("Afbeelding gekoppeld vanuit Media Library");
  };

  const onRemove = () => {
    onChange?.("", null);
    toast.message("Afbeelding ontkoppeld van item");
  };

  const previewBoxClass = compact
    ? "w-16 h-16 border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden"
    : "w-32 h-32 border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden";

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-root`}>
      {label && (
        <Label className="text-xs uppercase font-bold">{label}</Label>
      )}
      <div className="flex items-start gap-3 flex-wrap">
        {/* Preview */}
        <div className={previewBoxClass} data-testid={`${testIdPrefix}-preview`}>
          {hasImage ? (
            <img
              src={previewSrc(value)}
              alt={label}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              data-testid={`${testIdPrefix}-preview-img`}
            />
          ) : (
            <div className="flex flex-col items-center text-gray-400">
              <ImageIcon className={compact ? "w-5 h-5" : "w-8 h-8"} />
              {!compact && (
                <span className="text-[10px] uppercase mt-1">Geen</span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-1 min-w-[160px]">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT_MIME}
            onChange={onFileChange}
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={triggerFile}
              disabled={uploading}
              className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs"
              data-testid={`${testIdPrefix}-upload-button`}
            >
              {uploading ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5 mr-1" />
              )}
              {uploading ? "Uploaden…" : "Uploaden"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPickerOpen(true)}
              className="rounded-none font-chivo font-bold uppercase text-xs"
              data-testid={`${testIdPrefix}-library-button`}
            >
              <FolderOpen className="w-3.5 h-3.5 mr-1" /> Kies uit Media
            </Button>
            {hasImage && (
              <Button
                type="button"
                variant="outline"
                onClick={onRemove}
                className="rounded-none font-chivo font-bold uppercase text-xs text-red-600"
                data-testid={`${testIdPrefix}-remove-button`}
              >
                <X className="w-3.5 h-3.5 mr-1" /> Verwijderen
              </Button>
            )}
          </div>

          {/* Helper line */}
          {helperText && (
            <p className="text-[11px] text-gray-500" data-testid={`${testIdPrefix}-helper`}>
              {helperText}
            </p>
          )}

          {/* Advanced: URL fallback */}
          <button
            type="button"
            onClick={() => setShowUrl((s) => !s)}
            className="text-[11px] text-gray-500 hover:text-[#0055FF] underline-offset-2 hover:underline self-start flex items-center gap-1"
            data-testid={`${testIdPrefix}-toggle-url`}
          >
            <LinkIcon className="w-3 h-3" />
            {showUrl ? "Verberg URL-veld" : "Geavanceerd: afbeelding via URL"}
          </button>
          {showUrl && (
            <Input
              value={value || ""}
              onChange={(e) => onChange?.(e.target.value, null)}
              placeholder="https://example.com/image.jpg of /api/media/<id>"
              className="rounded-none text-xs"
              data-testid={`${testIdPrefix}-url-input`}
            />
          )}
        </div>
      </div>

      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={onLibrarySelect}
      />
    </div>
  );
}
